import { SystemEntity } from '@erp/shared-types';
import { cn } from '@/lib/utils';
import { getEntityIcon, getEntityDisplayName } from '../utils/index';
import { SupplierPreview } from './supplier-preview';
import { PurchaseOrderPreview } from './purchase-order-preview';
import { PurchaseOrderLineItemPreview } from './purchase-order-line-item-preview';
import { PaymentPreview } from './payment-preview';
import { FinancialTransactionPreview } from './financial-transaction-preview';
import { APInvoicePreview } from './ap-invoice-preview';
import { OutboundEmailPreview } from './outbound-email-preview';
import { ChargePreview } from './charge-preview';
import { ShipmentPreview } from './shipment-preview';
import { SalesOrderPreview } from './sales-order-preview';
import { InboundEmailPreview } from './inbound-email-preview';

interface SystemEntityPreviewProps {
  entityType: SystemEntity;
  entityId: string;
  className?: string;
}

function FallbackPreview({ entityType, className }: SystemEntityPreviewProps) {
  const Icon = getEntityIcon(entityType);
  const displayName = getEntityDisplayName(entityType);

  return (
    <div className={cn('min-w-64 max-w-sm p-3', className)}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="size-4 text-muted-foreground" />
        <h4 className="text-sm font-semibold">{displayName}</h4>
      </div>
      <p className="text-xs text-muted-foreground">
        No preview is available at this time
      </p>
    </div>
  );
}

/**
 * Component renderers for each system entity type
 * Maps entity types to their preview UI representations
 */
const entityRenderers: Partial<
  Record<SystemEntity, (props: SystemEntityPreviewProps) => React.ReactNode>
> = {
  [SystemEntity.AP_SUPPLIER]: (props) => <SupplierPreview {...props} />,
  [SystemEntity.INTERNAL_PURCHASE_ORDER]: (props) => (
    <PurchaseOrderPreview {...props} />
  ),
  [SystemEntity.INTERNAL_PURCHASE_ORDER_LINE_ITEM]: (props) => (
    <PurchaseOrderLineItemPreview {...props} />
  ),
  [SystemEntity.PAYMENT]: (props) => <PaymentPreview {...props} />,
  [SystemEntity.FINANCIAL_TRANSACTION]: (props) => (
    <FinancialTransactionPreview {...props} />
  ),
  [SystemEntity.AP_INVOICE]: (props) => <APInvoicePreview {...props} />,
  [SystemEntity.OUTBOUND_EMAIL]: (props) => <OutboundEmailPreview {...props} />,
  [SystemEntity.CHARGE]: ({ entityId, className }) => (
    <ChargePreview entityId={entityId} className={className} />
  ),
  [SystemEntity.SCM_SHIPMENT]: (props) => <ShipmentPreview {...props} />,
  [SystemEntity.AR_SALES_ORDER]: (props) => <SalesOrderPreview {...props} />,
  [SystemEntity.INBOUND_EMAIL]: (props) => <InboundEmailPreview {...props} />,
};

/**
 * Preview UI component for system entities
 * Renders a card-based preview for use in hover states
 * Designed to be used with HoverCard from @radix-ui
 */
export function SystemEntityPreview({
  entityType,
  entityId,
  className,
}: SystemEntityPreviewProps) {
  const normalizedEntityType = entityType as SystemEntity;
  const Renderer = entityRenderers[normalizedEntityType];

  if (Renderer) {
    return (
      <>{Renderer({ entityType: normalizedEntityType, entityId, className })}</>
    );
  }

  return (
    <FallbackPreview
      entityType={normalizedEntityType}
      entityId={entityId}
      className={className}
    />
  );
}
